import React, { useEffect, useState } from "react";
import Logo from "../../assets/main_logo.jpg";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { IoIosArrowDropright } from "react-icons/io";
import axios from "axios";
import { BASE_URL } from "../../utils/endpoints";
import { toast } from "react-toastify";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useGlobalContext } from "../../context/stateContext";
import { RotatingLines } from "react-loader-spinner";
import { useAuth } from "../../context/Auth";

const LoginPage = () => {
  const location = useLocation();
  const { requestWithAuth } = useAuth();
  const { isAuthenticated } = useGlobalContext();
  const { activeSession } = useGlobalContext();
  const [loginData, setLoginData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);


  const updateLoginData = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value,
    });
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    const body = JSON.stringify(loginData);

    try {
      const response = await axios.post(
        `${BASE_URL}/authentication/login/`,
        body,
        config
      );
      document.cookie = `accessToken=${response.data.access}; SameSite=Strict; Secure; path=/`;
      document.cookie = `refreshToken=${response.data.refresh}; SameSite=Strict; Secure; path=/`;

      toast.success("Logged In. Redirecting...");
      if (response.data.user.role !== "dispenser" && response.data.user.role !== "branch_manager") {
        createLoginSession(response);
      } else {
        redirectUser(response.data.user.role);
      }
    } catch (error) {
      if (error.response) {
        if (error.response.data && error.response.data[0]) {
          toast.error(error.response.data[0]);
        } else {
          toast.error("Invalid credentials...Please try again");
        }
      } else if (error.request) {
        toast.error("Network error. Please try again later.");
      } else {
        toast.error("An unexpected error occurred. Please try again later.");
      }
      setIsLoading(false);
    }
  };

  const createLoginSession = async (response) => {
    try {
      const body = {};
      const data = await requestWithAuth(`/users/session/`, "POST", body, {
        "Content-Type": "application/json",
      });
      document.cookie = `activesession=${data.id}; SameSite=Strict; Secure; path=/`;

      redirectUser(response.data.user.role);
    } catch (error) {
      toast.error("Failed to create session. Please try again.");
      setIsLoading(false);
    }
  };

  const redirectUser = (role) => {
    switch (role) {
      case "superadmin":
        window.location.href = "/super-admin/dashboard";
        break;
      case "dispenser":
        window.location.href = "/dispenser/verify-branch";
        break;
      case "admin":
        window.location.href = "/admin/dashboard";
        break;
      case "it_auditor":
        window.location.href = "/auditor/dashboard";
        break;
      case "warehouse_manager":
        window.location.href = "/warehouse-manager/dashboard";
        break;
      case "branch_manager":
        window.location.href = "/dispenser/verify-branch";
        break;
      case "stores":
        window.location.href = "/store-manager/dashboard";
        break;
      default:
        toast.error("Invalid Role");
        setIsLoading(false);
        break;
    }
  };

  if (isAuthenticated) {
    if (activeSession) {
      redirectUser(activeSession?.user?.role);
    }
  }

  
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get("reason") === "elsewhere") {
      toast.info("You were logged out because you logged in on another device.");
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-indigo-500/10 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 opacity-50"></div>

      <div className="relative z-10 min-h-screen flex">
        {/* Left Panel - Branding */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 flex-col justify-center items-start px-16 xl:px-24">
          <div className="max-w-lg">
            {/* Logo */}
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-500 rounded-2xl flex items-center justify-center mr-4 shadow-2xl">
                <img src={Logo} alt="" />
              </div>
              <h1 className="text-3xl font-bold text-white">LifeChemist</h1>
            </div>

            {/* Welcome Message */}
            <div className="mb-12">
              <h2 className="text-5xl xl:text-6xl font-bold text-white leading-tight mb-6">
                Welcome to the
                <span className="block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Future of Pharmacy
                </span>
              </h2>
              <p className="text-xl text-blue-100 leading-relaxed">
                Streamline your pharmaceutical operations with our comprehensive
                management system. Track inventory, process sales, and manage
                customer relationships all in one place.
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              {[
                "Real-time inventory tracking",
                "Automated prescription management",
                "Advanced analytics & reporting",
                "Multi-role access control",
              ].map((feature, index) => (
                <div key={index} className="flex items-center text-blue-100">
                  <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full mr-3"></div>
                  <span className="text-lg">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-400 to-purple-500 rounded-2xl mb-4 shadow-2xl">
                <svg
                  className="w-8 h-8 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                LifeChemist
              </h1>
              <p className="text-blue-200">Pharmacy Management System</p>
            </div>

            {/* Login Card */}
            <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 relative">
              {/* Decorative elements */}
              <div className="absolute -top-2 -right-2 w-20 h-20 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full blur-2xl opacity-20"></div>
              <div className="absolute -bottom-2 -left-2 w-16 h-16 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full blur-xl opacity-20"></div>

              <div className="relative z-10">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Welcome Back
                  </h3>
                  <p className="text-gray-600">
                    Please sign in to your account
                  </p>
                </div>

                <form onSubmit={onLogin} className="space-y-6">
                  {/* Username Field */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Username
                    </label>
                    <div className="relative group">
                      <div
                        className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200 ${focusedField === "username"
                          ? "text-blue-500"
                          : "text-gray-400"
                          }`}
                      >
                        <FaUser className="h-5 w-5" />
                      </div>
                      <input
                        name="username"
                        type="text"
                        required
                        onChange={updateLoginData}
                        onFocus={() => setFocusedField("username")}
                        onBlur={() => setFocusedField(null)}
                        placeholder="Enter your username"
                        disabled={isLoading}
                        className={`w-full pl-12 pr-4 py-4 bg-gray-50/50 border-2 rounded-xl text-gray-800 placeholder-gray-500 transition-all duration-200 outline-none ${focusedField === "username"
                          ? "border-blue-500 bg-white shadow-lg shadow-blue-500/10 transform scale-[1.02]"
                          : "border-gray-200 hover:border-gray-300 hover:bg-white"
                          } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Password
                    </label>
                    <div className="relative group">
                      <div
                        className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-200 ${focusedField === "password"
                          ? "text-blue-500"
                          : "text-gray-400"
                          }`}
                      >
                        <FaLock className="h-5 w-5" />
                      </div>
                      <input
                        name="password"
                        type={showPassword ? "text" : "password"}
                        onChange={updateLoginData}
                        required
                        onFocus={() => setFocusedField("password")}
                        onBlur={() => setFocusedField(null)}
                        placeholder="Enter your password"
                        disabled={isLoading}
                        className={`w-full pl-12 pr-12 py-4 bg-gray-50/50 border-2 rounded-xl text-gray-800 placeholder-gray-500 transition-all duration-200 outline-none ${focusedField === "password"
                          ? "border-blue-500 bg-white shadow-lg shadow-blue-500/10 transform scale-[1.02]"
                          : "border-gray-200 hover:border-gray-300 hover:bg-white"
                          } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isLoading}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {showPassword ? (
                          <FaEyeSlash className="h-5 w-5" />
                        ) : (
                          <FaEye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Remember Me */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        id="remember"
                        type="checkbox"
                        disabled={isLoading}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                      />
                      <label
                        htmlFor="remember"
                        className={`ml-2 text-sm text-gray-600 cursor-pointer ${isLoading ? "opacity-50" : ""
                          }`}
                      >
                        Keep me signed in
                      </label>
                    </div>
                    <Link
                      to="/forgot-password"
                      className={`text-sm text-blue-600 hover:text-blue-500 font-medium transition-colors duration-200 ${isLoading ? "pointer-events-none opacity-50" : ""
                        }`}
                    >
                      Forgot password?
                    </Link>
                  </div>

                  {/* Login Button */}
                  {isLoading ? (
                    <div className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold flex items-center justify-center gap-3 shadow-xl">
                      <RotatingLines
                        visible={true}
                        height="20"
                        width="20"
                        color="transparent"
                        strokeColor="white"
                        strokeWidth="3"
                        animationDuration="0.75"
                        ariaLabel="rotating-lines-loading"
                      />
                      "Signing you in...
                    </div>
                  ) : (
                    <button
                      type="submit"
                      className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold flex items-center justify-center gap-2 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 active:scale-95 transition-all duration-200"
                    >
                      Sign In to Dashboard
                      <IoIosArrowDropright className="w-5 h-5" />
                    </button>
                  )}
                </form>

                {/* Footer */}
                <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                  <p className="text-xs text-gray-400 mt-2">
                    © LifeChemist. All rights reserved.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
